import { useMemo } from "react";
import {
  FieldValues,
  Path,
  ControllerProps,
  UseFormReturn,
} from "react-hook-form";
import { ObjectSchema } from "yup";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import { FormFieldWrapperProps } from "components/Shared/Forms/FormFieldWrapper";
import { assertPresence } from "utils/assertions";
import {
  FilterDefinition,
  FilterVariantDefinition,
  getIsControlledInputFilterDefinition,
  getIsControlledInputFilterVariantDefinition,
} from "./utils";

export type FilterInputProps<TFieldValues extends FieldValues> =
  FormFieldWrapperProps & {
    filterDefinition: FilterDefinition | undefined;
    filterVariantDefinition: FilterVariantDefinition | undefined;
    fieldName: Path<TFieldValues>;
    formContext: UseFormReturn<TFieldValues>;
    schema: ObjectSchema<TFieldValues>;
    label?: string | null;
    isDisabled?: boolean;
  };

/**
 * Component that renders a filter input using the same logic as FilterEditor.
 * Extracted from components/Shared/FilterEditor/Filter.tsx lines 185-208
 */
export default function FilterInput<TFieldValues extends FieldValues>({
  filterDefinition,
  filterVariantDefinition,
  fieldName,
  formContext,
  schema,
  isDisabled = false,
  ...fieldProps
}: FilterInputProps<TFieldValues>) {
  const { FormField, ControlledFormField } = useMemo(
    () => createFormFieldComponents<TFieldValues>({ schema }),
    [schema],
  );

  const isControlledInputVariant =
    filterVariantDefinition &&
    getIsControlledInputFilterVariantDefinition(filterVariantDefinition);
  const isControlledInputFilter =
    filterDefinition && getIsControlledInputFilterDefinition(filterDefinition);
  const isControlledInput = isControlledInputVariant || isControlledInputFilter;

  const commonFieldProps = {
    name: fieldName,
    isDisabled,
    ...fieldProps,
  };

  if (isControlledInput) {
    return (
      <ControlledFormField
        {...commonFieldProps}
        renderInput={
          ((isControlledInputVariant &&
            filterVariantDefinition.renderControlledInput) ||
            (isControlledInputFilter &&
              filterDefinition.renderControlledInput)) as unknown as ControllerProps<
            TFieldValues,
            Path<TFieldValues>
          >["render"]
        }
      />
    );
  }

  return (
    <FormField
      {...commonFieldProps}
      renderInput={
        filterVariantDefinition?.renderInput || filterDefinition?.renderInput
      }
      InputComponent={
        filterVariantDefinition?.InputComponent ||
        filterDefinition?.InputComponent
      }
    />
  );
}

type DefaultVariantFilterInputProps<TFieldValues extends FieldValues> = Omit<
  FilterInputProps<TFieldValues>,
  "filterVariantDefinition"
> & {
  filterDefinition: FilterDefinition;
};

export function DefaultVariantFilterInput<TFieldValues extends FieldValues>(
  props: DefaultVariantFilterInputProps<TFieldValues>,
) {
  const { filterDefinition } = props;
  const { id, variants, defaultVariant } = filterDefinition;

  const variantId = defaultVariant || variants[0]?.id;
  const variant = variants.find((v) => v.id === variantId);
  assertPresence(variant, `Variant '${variantId}' for filter '${id}'`);

  return <FilterInput {...props} filterVariantDefinition={variant} />;
}
